/**
 * Native (Capacitor) background token-refresh wiring.
 *
 * supabase-js' `autoRefreshToken` timer (a 30s `setInterval` tick) is suspended
 * by the OS while a native app sits in the background, so the access_token can
 * silently expire. The documented fix (Supabase React Native / Capacitor guide)
 * is to STOP the timer when the app backgrounds and START it again on resume -
 * starting also forces an immediate refresh-check on foreground.
 *
 * Web is unaffected: the browser keeps the SDK's timer alive, so we only wire
 * this on native platforms. Per-call `getFreshUserToken()` (api.ts) already
 * covers the edge-function path; this additionally keeps the realtime channel's
 * auth warm so a backgrounded->resumed app does not drop its subscription.
 *
 * Verified against Context7 /supabase/supabase-js (GoTrueClient
 * start/stopAutoRefresh; 30s AUTO_REFRESH_TICK_DURATION_MS).
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Wire the Capacitor app-lifecycle to supabase-js' auto-refresh timer.
 *
 * @returns a cleanup function that removes the listener (no-op on web).
 */
export function wireNativeAutoRefresh(client: SupabaseClient): () => void {
  if (!Capacitor.isNativePlatform()) {
    return () => {};
  }

  // The app is typically active right after launch - make sure the timer runs.
  void client.auth.startAutoRefresh();

  const handlePromise = App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      void client.auth.startAutoRefresh();
    } else {
      void client.auth.stopAutoRefresh();
    }
  });

  return () => {
    void Promise.resolve(handlePromise).then((handle) => handle.remove());
  };
}
